import React, { useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import DragIndicatorIcon from 'icons/drag-indicator.svg';
import LockIcon from 'icons/ic-lock-12.svg';
import RestrictedIcon from 'icons/ic-restricted-16.svg';
import VisibleIcon from 'icons/ic-visible-16.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CheckboxField from 'components/application/CheckboxField';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import Switch from 'components/application/Switch';
import Tooltip from 'components/application/Tooltip';

import DestroyModal from 'components/settings/personnel/information/DestroyModal';

export default function FieldRow({
  fieldAttribute,
  fieldSetting,
  onDestroyFieldAttribute,
  onDestroyFieldValues,
  onEdit,
  onIncludedToggle,
  onRestrictedToggle,
}) {
  const {
    id: fieldSettingId,
    included: isIncluded,
    restricted: isRestricted,
  } = fieldSetting;

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: fieldSettingId });

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.personnel.information.FieldRow',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    default: isDefault,
    fieldValueCount,
    formattedFieldType,
    name,
  } = fieldAttribute;

  return (
    <div
      className={classNames(
        'row-bar row-bar--flex-align-items-ie-fix tw-group/row tw-border-grey-100 hover:tw-bg-grey-025',
        isDragging ? 'tw-z-[2]' : 'tw-border-t-0',
      )}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className='tw-w-13'>
        <div className='tw-flex tw-items-center'>
          <div
            className={classNames(
              '-tw-ml-3 tw-flex tw-h-9 tw-w-5 tw-touch-none tw-items-center tw-justify-center group-hover/row:tw-visible hover-hover:tw-invisible',
              isDragging ? 'tw-cursor-grabbing' : 'tw-cursor-grab',
            )}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon
              className='[&_path]:tw-fill-grey-700'
              height={12}
              width={8}
            />
          </div>
          <div className='tw-ml-2'>
            <CheckboxField
              checked={isIncluded}
              height='short'
              labelTextProps={{ className: 'tw-font-medium' }}
              name='included'
              onChange={() => onIncludedToggle({ fieldSetting })}
            />
          </div>
        </div>
      </div>
      <div className='tw-min-w-0 tw-flex-1'>
        <div className='tw-flex tw-items-center'>
          <div className='truncated-text-container truncated-text-container--auto tw-mr-2 tw-text-m tw-font-medium tw-text-grey-900'>
            {name}
          </div>
          {isDefault && (
            <div className='tw-mr-2 tw-inline-flex tw-h-5 tw-items-center tw-rounded-md tw-bg-grey-100 tw-px-1 tw-py-0.5 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-600'>
              <LockIcon
                className='tw-mr-0.5 [&_path]:tw-fill-grey-600'
                height={12}
                width={12}
              />
              <span>{i18nTCommon('shared.default')}</span>
            </div>
          )}
        </div>
      </div>
      <div className='tw-w-37 tw-text-m'>
        {i18nTCommon(`personnel.fieldAttributeTypes.${formattedFieldType}`)}
      </div>
      <div className='tw-w-30 tw-text-m'>
        {isRestricted ?
          <div className='tw-flex tw-items-center'>
            <RestrictedIcon className='tw-mr-2 [&_path]:tw-fill-red-400' />
            <span>{i18nTCommon('shared.restricted')}</span>
          </div>
        : <div className='tw-flex tw-items-center'>
            <VisibleIcon className='tw-mr-2 [&_path]:tw-fill-grey-600' />
            <span>{i18nTCommon('shared.visible')}</span>
          </div>
        }
      </div>
      <div className='tw-w-22 tw-pr-8 tw-text-m'>
        <div className='tw-flex tw-items-center tw-justify-center'>
          {fieldValueCount}
        </div>
      </div>
      <div className='tw-w-12'>
        <Options>
          <OptionChoice
            disabled={isDefault}
            onClick={(setOptionsOpen) => {
              setOptionsOpen(false);
              onEdit({ id: fieldSettingId });
            }}
          >
            <ConditionalWrapper
              condition={isDefault}
              wrapper={(children) => (
                <Tooltip
                  className='tooltip-dark--max-w-xxs'
                  placement='left'
                  tooltip={i18nT('optionChoices.editField.tooltip')}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                {i18nT('optionChoices.editField.button')}
              </a>
            </ConditionalWrapper>
          </OptionChoice>
          <OptionChoice
            hoverClassName='hover:tw-bg-blue-025'
            onClick={() => onRestrictedToggle({ fieldSetting })}
          >
            <a className='tw-group/option tw-flex tw-items-center tw-gap-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
              <span className='tw-inline-block'>
                {i18nTCommon('shared.restricted?')}
              </span>
              <Switch isChecked={isRestricted} />
            </a>
          </OptionChoice>
          {isDefault ?
            <OptionChoice
              disabled={fieldValueCount === 0}
              onClick={() => setIsDestroyModalOpen(true)}
              warning={true}
            >
              <ConditionalWrapper
                condition={fieldValueCount === 0}
                wrapper={(children) => (
                  <Tooltip
                    className='tooltip-dark--max-w-xxs'
                    placement='left'
                    tooltip={i18nT('optionChoices.deleteData.tooltip')}
                    trigger='hover'
                  >
                    {children}
                  </Tooltip>
                )}
              >
                <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-red-600 hover:tw-text-red-600'>
                  {i18nT('optionChoices.deleteData.button')}
                </a>
              </ConditionalWrapper>
            </OptionChoice>
          : <OptionChoice
              onClick={() => setIsDestroyModalOpen(true)}
              warning={true}
            >
              <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-red-600 hover:tw-text-red-600'>
                {i18nTCommon('shared.delete')}
              </a>
            </OptionChoice>
          }
        </Options>
        <DestroyModal
          fieldAttribute={fieldAttribute}
          isOpen={isDestroyModalOpen}
          onDestroy={() => {
            isDefault ?
              onDestroyFieldValues({ fieldAttribute })
            : onDestroyFieldAttribute({ fieldSetting, fieldAttribute });
          }}
          setClose={() => setIsDestroyModalOpen(false)}
        />
      </div>
    </div>
  );
}

FieldRow.propTypes = {
  fieldAttribute: PropTypes.exact({
    default: PropTypes.bool.isRequired,
    fieldValueCount: PropTypes.number.isRequired,
    formattedFieldType: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  fieldSetting: PropTypes.exact({
    id: PropTypes.string.isRequired,
    included: PropTypes.bool.isRequired,
    restricted: PropTypes.bool.isRequired,
  }).isRequired,
  onDestroyFieldAttribute: PropTypes.func.isRequired,
  onDestroyFieldValues: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onIncludedToggle: PropTypes.func.isRequired,
  onRestrictedToggle: PropTypes.func.isRequired,
};
